import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { CaretDown, CaretUp, Eye, EyeSlash } from '@phosphor-icons/react';
import { IPropsUserWallet } from '../../../../@types';
import { filterTableSorte } from '../../../../../../../utils/filterTableSort';
import { Tooltip } from 'antd';
import { TypeActiveContent } from '../../../../../../components/AppsMenu/AppProfile/stylePostDetails';
import { transformMaskCurrency } from '../../../../../../../utils/transformMaskCurrency';
import { validateFloatNumber } from '../../../../../../components/Boltz/ValidateFloatNumber';
import { defineDisplayColor } from '../../../../../../components/Boltz/DefineDisplayColor';
import { useState } from 'react';
import { useActives } from '../../../../../../../hooks/useActives';
import { CompanyNameContent } from './styles';
import { GraphicNote } from './GraphicNote';
import { RenderConditionalObjective } from '../../../../../../components/RenderConditionalObjective';
import { useActivesWallet } from '../../../../hooks/ActivesHooks';
import { BiInfoCircle } from 'react-icons/bi';
import {
  BoxPctMap,
  EMPTY_BOX_PCT_MAP,
  applyBoxAllocation,
  isVisibleAfterBox,
} from '../../boxAllocation';

// Rótulos e ordem do detalhamento da nota (score_items) dos ativos
// internacionais STOCK/REIT. As chaves vêm de `nota_items` no payload
// (backend: international_assets/services/asset_score_service).
const NOTA_ITEMS_LABELS: Record<string, string> = {
  net_income_score: 'Lucro líquido',
  ebitda_score: 'EBITDA',
  net_debt_score: 'Dívida líquida',
  net_debt_ebitda_score: 'Dívida/EBITDA',
  dividend_score: 'Dividendos',
};

// Monta o conteúdo do tooltip da coluna Nota. Ativos com `nota_items`
// (STOCK/REIT internacionais) ganham o breakdown por métrica; os demais
// mantêm o texto simples de antes.
function buildNotaTooltip(item: any) {
  const nota = Number(item?.nota) || 0;
  if (nota <= 0) {
    return 'Nota não aplicável a esse ativo ou não encontrada';
  }

  const items = item?.nota_items;
  const hasBreakdown =
    items &&
    typeof items === 'object' &&
    Object.keys(NOTA_ITEMS_LABELS).some((key) => items[key] != null);

  if (!hasBreakdown) {
    return `${nota.toFixed(0)}%`;
  }

  return (
    <div style={{ minWidth: 190 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Nota: {nota.toFixed(0)}%
      </div>
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.25)',
          paddingTop: 4,
        }}
      >
        {Object.entries(NOTA_ITEMS_LABELS).map(([key, label]) =>
          items[key] != null ? (
            <div
              key={key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <span>{label}</span>
              <span>{Number(items[key]).toFixed(1)}</span>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#FFF',
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const header = [
  {
    label: 'Ativo',
    active: 'tickerSymbol',
  },
  {
    label: 'Tipo',
    active: 'productTypeName',
  },
  {
    label: 'Nota',
    active: 'nota',
  },
  {
    label: 'Cotação',
    active: 'closingPrice',
  },
  {
    label: 'Quant.',
    active: 'equitiesQuantity',
  },
  {
    label: 'Total investido',
    active: 'Total investido',
    help: 'Valor total aportado no ativo ao longo do tempo.',
  },
  {
    label: 'Patrimônio',
    active: 'updateValue',
    help: 'Valor atual investido no ativo.',
  },
  { label: 'Objetivo', help: 'Percentual desejado de alocação na carteira.' },
  {
    label: 'Atual',
    active: 'percent',
    help: 'Percentual atual de alocação na carteira.',
  },
  {
    label: 'Diferença',
    active: 'gap',
    help: 'Diferença entre o percentual atual e o objetivo.',
  },
  {
    label: 'Preço médio',
    active: 'PrecoMedio',
    help: 'O preço médio é calculado somando-se o custo total de todas as compras de um ativo e dividindo-o pelo número total de ações ou cotas adquiridas.',
  },
  {
    label: 'Preço médio com proventos',
    active: 'Preco médio com proventos',
    help: 'Preco médio subtraindo os dividendos/proventos por cota recebidos.',
  },
  {
    label: 'Total de proventos recebidos',
    active: 'Total de proventos recebidos',
    help: 'Soma total de dividendos, JCP e rendimentos recebidos.',
  },
  {
    label: 'Rentabilidade com proventos',
    active: 'Rentabilidade com proventos',
    help: 'Rentabilidade líquida considerando valorização e proventos.',
  },
  {
    label: 'Lucro/Prejuízo',
    active: 'lucro',
    help: 'Rentabilidade considerando o valor investido e os dividendos/proventos recebidos.',
  },
];

interface IPropsAllTable {
  handleOrientationTable: Function;
  fullWallet: IPropsUserWallet;
  filterShowColumnTable: string[];
  selectedBrokerage: string;
  setRule: Function;
  setShowModalCreateRule: Function;
  filterTextSearchActive: string;
  onOperateAsset?: (item: any) => void;
  boxPct?: BoxPctMap;
}

export function AllTable({
  handleOrientationTable,
  fullWallet,
  filterShowColumnTable,
  selectedBrokerage,
  setRule,
  setShowModalCreateRule,
  filterTextSearchActive,
  onOperateAsset,
  boxPct = EMPTY_BOX_PCT_MAP,
}: IPropsAllTable) {
  //@ts-ignore
  const { showActive } = useActives();
  const { typeWallet } = useActivesWallet();
  const [showAmountActives, setShowAmountActives] = useState<boolean>(false);
  const [showActiveName, setShowActiveName] = useState<boolean>(false);
  const showActiveColumnTable = filterShowColumnTable.includes('Ativo');
  const showTypeColumnTable = filterShowColumnTable.includes('Tipo');
  const showNoteColumnTable = filterShowColumnTable.includes('Nota');
  const showClosePriceColumnTable = filterShowColumnTable.includes('Cotação');
  const showAmountColumnTable = filterShowColumnTable.includes('Quant.');
  const showPatrimonyColumnTable = filterShowColumnTable.includes('Patrimônio');
  const showGoalColumnTable = filterShowColumnTable.includes('Objetivo');
  const showCurrentColumnTable = filterShowColumnTable.includes('Atual');
  const showGapColumnTable = filterShowColumnTable.includes('Diferença');
  const showProfitColumnTable =
    filterShowColumnTable.includes('Lucro/Prejuízo');
  const showTotalApliedColumnTable =
    filterShowColumnTable.includes('Total investido');
  //aqui
  const showRealProfitabilityColumnTable = filterShowColumnTable.includes(
    'Rentabilidade com proventos'
  );
  const showAveragePriceColumnTable =
    filterShowColumnTable.includes('Preço médio');
  const showTotalProfits = filterShowColumnTable.includes(
    'Total de proventos recebidos'
  );

  const showAveragePriceWithProfitsColumnTable = filterShowColumnTable.includes(
    'Preço médio com proventos'
  );

  const ArrayFilteredAllTabe = fullWallet?.positionsAll?.filter((item) => {
    if (
      (item.tickerSymbol
        .toLowerCase()
        .includes(filterTextSearchActive.toLocaleLowerCase()) ||
      item.corporationName
        .toLowerCase()
        .includes(filterTextSearchActive.toLocaleLowerCase()))
    ) {
      return item;
    }
  });

  // Desconta a parcela alocada em boxes (objetivos/reservas): ativos 100%
  // alocados saem da listagem; parcialmente alocados aparecem só com a sobra.
  const displayPositions = (ArrayFilteredAllTabe ?? [])
    .map((item) => applyBoxAllocation(item, boxPct))
    .filter(isVisibleAfterBox);

  // console.log(
  //   'DEBUG positionsAll:',
  //   fullWallet?.positionsAll?.map((item) => ({
  //     ticker: item.tickerSymbol,
  //     preco_medio_sem_proventos: item.preco_medio_sem_proventos,
  //     preco_medio_com_proventos: item.preco_medio_com_proventos,
  //     quantidade: item.equitiesQuantity,
  //     total_proventos: item.total_proventos,
      
  //   }))
  // );

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table aria-label="customized table" stickyHeader>
        <TableHead>
          <TableRow>
            {header.map((item) => {
              if (filterShowColumnTable.includes(item.label)) {
                return (
                  <StyledTableCell key={item.label}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.625rem',
                        fontSize: '0.95rem',
                      }}
                    >
                      {item.label}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {item?.help && (
                          <Tooltip title={item.help}>
                            <BiInfoCircle size={16} color="#EC6643" />
                          </Tooltip>
                        )}
                      </div>
                      {item.active && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <CaretUp
                            size={14}
                            color="black"
                            onClick={() => {
                              handleOrientationTable({
                                ...fullWallet,
                                positionAcao: filterTableSorte({
                                  data: fullWallet.positionsAll,
                                  dir: 'asc',
                                  //@ts-ignore
                                  key: item.active,
                                }),
                              });
                            }}
                          />
                          <CaretDown
                            size={14}
                            color="black"
                            onClick={() => {
                              handleOrientationTable({
                                ...fullWallet,
                                positionAcao: filterTableSorte({
                                  data: fullWallet.positionsAll,
                                  dir: 'desc',
                                  //@ts-ignore
                                  key: item.active,
                                }),
                              });
                            }}
                          />
                        </div>
                      )}
                      {item.label === 'Quant.' &&
                        (showAmountActives ? (
                          <Eye
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowAmountActives(!showAmountActives)
                            }
                          />
                        ) : (
                          <EyeSlash
                            size={20}
                            color="black"
                            onClick={() =>
                              setShowAmountActives(!showAmountActives)
                            }
                          />
                        ))}
                      {item.label === 'Ativo' &&
                        (showActiveName ? (
                          <Eye
                            size={20}
                            color="black"
                            onClick={() => setShowActiveName(!showActiveName)}
                          />
                        ) : (
                          <EyeSlash
                            size={20}
                            color="black"
                            onClick={() => setShowActiveName(!showActiveName)}
                          />
                        ))}
                    </div>
                  </StyledTableCell>
                );
              }
            })}
            {onOperateAsset && (
              <StyledTableCell>
                <div style={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                  Operar
                </div>
              </StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayPositions.length > 0 ? (
            displayPositions.map((item, index) => (
              <StyledTableRow key={index}>
                {showActiveColumnTable && (
                  <StyledTableCell>
                    <CompanyNameContent>
                      {showActiveName || showActive ? (
                        <>
                          <strong title={item.tickerSymbol}>
                            {item.tickerSymbol}
                          </strong>
                          <span title={item.corporationName}>
                            {' '}
                            {item.corporationName}
                          </span>
                        </>
                      ) : (
                        '*****'
                      )}
                    </CompanyNameContent>
                  </StyledTableCell>
                )}
                {showTypeColumnTable && (
                  <StyledTableCell>
                    <TypeActiveContent
                      //@ts-ignore
                      typeActive={
                        item.productTypeName === 'acao'
                          ? 'acao'
                          : item.productTypeName === 'fii'
                          ? 'FII'
                          : ['stock', 'stocks'].includes(item.productTypeName)
                          ? 'stocks'
                          : ['reit', 'reits'].includes(item.productTypeName)
                          ? 'reits'
                          : ['etf', 'etfs'].includes(item.productTypeName)
                          ? 'etfs'
                          : ['crypto', 'cripto'].includes(item.productTypeName)
                          ? 'crypto'
                          : 'rendaFixa'
                      }
                    >
                      {item.productTypeName === 'acao'
                        ? 'Ação'
                        : item.productTypeName === 'fii'
                        ? 'FII'
                        : ['stock', 'stocks'].includes(item.productTypeName)
                        ? 'Stocks'
                        : ['reit', 'reits'].includes(item.productTypeName)
                        ? 'REITs'
                        : ['etf', 'etfs'].includes(item.productTypeName)
                        ? 'ETFs'
                        : ['crypto', 'cripto'].includes(item.productTypeName)
                        ? 'Criptomoedas'
                        : 'Renda Fixa'}
                    </TypeActiveContent>
                  </StyledTableCell>
                )}
                {showNoteColumnTable && (
                  <StyledTableCell>
                    <Tooltip title={buildNotaTooltip(item)}>
                      {item.nota <= 0 ? (
                        <>N/A</>
                      ) : (
                        <GraphicNote
                          noteValue={item.nota}
                          color={
                            item.nota <= 50
                              ? '#ff0000'
                              : item.nota <= 60
                              ? '#ffff00'
                              : '#0000ff'
                          }
                        />
                      )}
                    </Tooltip>
                  </StyledTableCell>
                )}
                {showClosePriceColumnTable && (
                  <StyledTableCell>
                    {transformMaskCurrency(item.closingPrice)}
                  </StyledTableCell>
                )}
                {showAmountColumnTable && (
                  <StyledTableCell>
                    {showActive || showAmountActives
                      ? validateFloatNumber(
                          item.equitiesQuantity.toFixed(2).replace('.', ',')
                        )
                      : '****'}
                  </StyledTableCell>
                )}
                {showTotalApliedColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(
                            item.equitiesQuantity *
                              item.preco_medio_sem_proventos
                          )
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(
                          item.equitiesQuantity * item.preco_medio_sem_proventos
                        )
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {showPatrimonyColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.updateValue)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.updateValue)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {showGoalColumnTable && (
                  // @ts-ignore
                  // <StyledTableCell>{item.ValorExp.toFixed(2)}%</StyledTableCell>

                  <RenderConditionalObjective
                    //@ts-ignore
                    active={item}
                    exceptions={item.Exception}
                    filterBrokerage={selectedBrokerage}
                    forceAllType
                    setRule={setRule}
                    setShowModalCreateRule={setShowModalCreateRule}
                    typeWallet={typeWallet}
                    //@ts-ignore
                    walletValues={fullWallet}
                  />
                )}
                {showCurrentColumnTable && (
                  <StyledTableCell
                  // style={{
                  //   color: defineDisplayColor({

                  //     item,
                  //     exceptions: item.Exception,
                  //     type: filterBrokerage === '' ? 'all' : 'broker',
                  //     wallet: fullWallet,
                  //     //@ts-ignore
                  //     filterBrokerage:
                  //       filterBrokerage === '' ? null : filterBrokerage,
                  //     typeWallet: typeWallet,
                  //   }),
                  // }}
                  >
                    {item?.percent?.toFixed(2)}%
                  </StyledTableCell>
                )}
                {showGapColumnTable && (
                  <StyledTableCell>
                    {(item.porcentagem_recomendada - item.percent).toFixed(2)}%
                  </StyledTableCell>
                )}
                {/* aqui é onde não aparece o preço médio */}
                {showAveragePriceColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.preco_medio_sem_proventos)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.preco_medio_sem_proventos)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {showAveragePriceWithProfitsColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.preco_medio_com_proventos)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.preco_medio_com_proventos)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {/* aqui total proventos recebidos */}
                {showTotalProfits && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(item.total_proventos)
                        : 'R$ ******'
                    }
                  >
                    {showActive
                      ? transformMaskCurrency(item.total_proventos)
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {/* {showProfitabilityColumnTable && (
                  <StyledTableCell
                    style={{
                      color:
                        item.porcentagem_rentabilidade_sem_proventos < 0
                          ? 'red'
                          : '#44bd32',
                    }}
                  >
                    {item?.porcentagem_rentabilidade_sem_proventos
                      ? `${item?.porcentagem_rentabilidade_sem_proventos}%`
                      : '-'}
                  </StyledTableCell>
                )} */}
                {/* aqui Rentabilidade com proventos */}
                {showRealProfitabilityColumnTable && (
                  <StyledTableCell
                    style={{
                      color:
                        item.porcentagem_rentabilidade_com_proventos < 0
                          ? 'red'
                          : '#44bd32',
                    }}
                  >
                    {item?.porcentagem_rentabilidade_com_proventos
                      ? `${item?.porcentagem_rentabilidade_com_proventos}%`
                      : '-'}
                  </StyledTableCell>
                )}
                {showProfitColumnTable && (
                  <StyledTableCell
                    className="reduce"
                    title={
                      showActive
                        ? transformMaskCurrency(
                            item.valor_rentabilidade_com_proventos
                          )
                        : 'R$ ******'
                    }
                    style={{
                      color:
                        item.valor_rentabilidade_com_proventos < 0
                          ? 'red'
                          : '#44bd32',
                    }}
                  >
                    {showActive
                      ? transformMaskCurrency(
                          item.valor_rentabilidade_com_proventos
                        )
                      : 'R$ ******'}
                  </StyledTableCell>
                )}
                {onOperateAsset && (
                  <StyledTableCell>
                    <button
                      type="button"
                      onClick={() => onOperateAsset(item)}
                      style={{
                        border: 'none',
                        background: '#EC6643',
                        color: '#FFF',
                        borderRadius: 6,
                        padding: '0.35rem 0.75rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Comprar / Vender
                    </button>
                  </StyledTableCell>
                )}
              </StyledTableRow>
            ))
          ) : (
            <h1 style={{ fontSize: '1rem', margin: '1rem 0.5rem' }}>
              Ainda não há dados
            </h1>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
